import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SUBS = Array.from({ length: 9 }, (_, i) => `sub_id_${i + 1}`);
const SCOPE_LABEL = { money: "Money (revenue / payout)", date: "Date / time", subs: "Sub IDs", status: "Conversion type", all: "All fields" };

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : "");

const cls = "h-8 rounded-md border border-white/[0.1] bg-[#0A0A0A] px-2 text-xs text-foreground outline-none focus:border-white/25";

export function ConversionBulkEditDialog({ open, onOpenChange, rows = [], scope = "all", onDone }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState("uniform");
  const [busy, setBusy] = useState(false);
  const [u, setU] = useState({ created_at: "", revenue: "", payout: "", status: "", payment_status: "" });
  const [uSubs, setUSubs] = useState({});
  const [perRow, setPerRow] = useState({});

  const showDate = scope === "date" || scope === "all";
  const showMoney = scope === "money" || scope === "all";
  const showStatus = scope === "status" || scope === "all";
  const showSubs = scope === "subs" || scope === "all";

  useEffect(() => {
    if (open) {
      setMode("uniform");
      setU({ created_at: "", revenue: "", payout: "", status: "" });
      setUSubs({});
      const map = {};
      rows.forEach((r) => {
        map[r.id] = {
          created_at: toLocalInput(r.created_at), revenue: r.revenue ?? "", payout: r.payout ?? "",
          status: r.status || "lead",
          ...Object.fromEntries(SUBS.map((k) => [k, r[k] || ""])),
        };
      });
      setPerRow(map);
    }
  }, [open, rows]);

  const setRowCell = (id, k, v) => setPerRow((m) => ({ ...m, [id]: { ...m[id], [k]: v } }));
  const ids = useMemo(() => rows.map((r) => r.id), [rows]);

  const buildItems = () => {
    if (mode === "uniform") {
      const common = {};
      if (showDate && u.created_at) common.created_at = fromLocalInput(u.created_at);
      if (showMoney && u.revenue !== "") common.revenue = u.revenue;
      if (showMoney && u.payout !== "") common.payout = u.payout;
      if (showStatus && u.status) common.status = u.status;
      if (showSubs) SUBS.forEach((k) => { if ((uSubs[k] || "") !== "") common[k] = uSubs[k]; });
      if (Object.keys(common).length === 0) return null;
      return ids.map((id) => ({ id, ...common }));
    }
    const items = [];
    rows.forEach((r) => {
      const e = perRow[r.id] || {};
      const item = { id: r.id };
      let any = false;
      if (showDate && e.created_at && e.created_at !== toLocalInput(r.created_at)) { item.created_at = fromLocalInput(e.created_at); any = true; }
      if (showMoney && String(e.revenue) !== String(r.revenue ?? "")) { item.revenue = e.revenue; any = true; }
      if (showMoney && String(e.payout) !== String(r.payout ?? "")) { item.payout = e.payout; any = true; }
      if (showStatus && e.status && e.status !== r.status) { item.status = e.status; any = true; }
      if (showSubs) SUBS.forEach((k) => { if ((e[k] || "") !== (r[k] || "")) { item[k] = e[k] || ""; any = true; } });
      if (any) items.push(item);
    });
    return items.length ? items : null;
  };

  const save = async () => {
    const items = buildItems();
    if (!items) return toast.error("Nothing to change", { description: "Enter at least one value." });
    setBusy(true);
    try {
      const { data } = await http.post("/conversions/bulk-edit", { items });
      qc.invalidateQueries({ queryKey: ["conversions"] });
      qc.invalidateQueries({ queryKey: ["balances"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["statistics"] });
      qc.invalidateQueries({ queryKey: ["stats-report"] });
      toast.success("Conversions updated", { description: `${data.changed} conversion(s) changed` });
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("Update failed", { description: e?.response?.data?.detail || "Try again." });
    } finally { setBusy(false); }
  };

  const wide = mode === "per_row" && (showSubs || scope === "all");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${wide ? "max-w-5xl" : "max-w-lg"} border-white/[0.1] bg-[#141417]`} data-testid="conv-bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="h-4 w-4" /> Edit · {SCOPE_LABEL[scope]}</DialogTitle>
          <DialogDescription>Editing {rows.length} selected conversion(s).</DialogDescription>
        </DialogHeader>

        <div className="inline-flex rounded-lg border border-white/[0.1] bg-[#0A0A0A] p-0.5 text-xs" data-testid="bulk-edit-mode">
          <button onClick={() => setMode("uniform")} data-testid="mode-uniform"
            className={`rounded-md px-3 py-1.5 transition-colors ${mode === "uniform" ? "bg-white text-black" : "text-muted-foreground hover:text-foreground"}`}>Same value for all</button>
          <button onClick={() => setMode("per_row")} data-testid="mode-per-row"
            className={`rounded-md px-3 py-1.5 transition-colors ${mode === "per_row" ? "bg-white text-black" : "text-muted-foreground hover:text-foreground"}`}>Per conversion</button>
        </div>

        {mode === "uniform" ? (
          <div className="space-y-4">
            <p className="text-[11px] text-muted-foreground/70">Leave a field empty to keep its current value. Filled fields apply to all {rows.length} selected.</p>
            {showDate && (
              <div>
                <label className="mb-1.5 block text-xs text-muted-foreground">Conversion date / time</label>
                <Input type="datetime-local" value={u.created_at} onChange={(e) => setU((s) => ({ ...s, created_at: e.target.value }))} className="h-9 border-white/[0.12] bg-[#0A0A0A] text-sm" data-testid="bulk-edit-date" />
              </div>
            )}
            {showMoney && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-xs text-muted-foreground">Revenue</label>
                  <Input type="number" value={u.revenue} onChange={(e) => setU((s) => ({ ...s, revenue: e.target.value }))} placeholder="keep" className="h-9 border-white/[0.12] bg-[#0A0A0A] text-sm" data-testid="bulk-edit-revenue" />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs text-muted-foreground">Payout</label>
                  <Input type="number" value={u.payout} onChange={(e) => setU((s) => ({ ...s, payout: e.target.value }))} placeholder="keep" className="h-9 border-white/[0.12] bg-[#0A0A0A] text-sm" data-testid="bulk-edit-payout" />
                </div>
              </div>
            )}
            {showStatus && (
              <div>
                <label className="mb-1.5 block text-xs text-muted-foreground">Conversion type</label>
                <select value={u.status} onChange={(e) => setU((s) => ({ ...s, status: e.target.value }))} className={`${cls} h-9 w-full text-sm`} data-testid="bulk-edit-status">
                  <option value="">Keep</option><option value="lead">Lead</option><option value="sale">Sale</option><option value="redep">Redep</option><option value="revshare">RevShare</option>
                </select>
              </div>
            )}
            {showSubs && (
              <div>
                <label className="mb-1.5 block text-xs text-muted-foreground">Sub IDs</label>
                <div className="grid grid-cols-3 gap-2">
                  {SUBS.map((k) => (
                    <Input key={k} value={uSubs[k] || ""} onChange={(e) => setUSubs((s) => ({ ...s, [k]: e.target.value }))}
                      placeholder={k.replace("sub_id_", "sub ")} className="h-8 border-white/[0.12] bg-[#0A0A0A] text-xs" data-testid={`bulk-edit-${k}`} />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="max-h-[56vh] overflow-auto rounded-lg border border-white/[0.08]">
            <table className="w-full border-collapse text-xs">
              <thead className="sticky top-0 z-10 bg-[#141417]">
                <tr className="border-b border-white/[0.08] text-left text-[10px] uppercase tracking-wider text-muted-foreground/70">
                  <th className="px-2 py-2 font-medium">Order ID</th>
                  {showDate && <th className="px-2 py-2 font-medium">Date / time</th>}
                  {showMoney && <th className="px-2 py-2 font-medium">Revenue</th>}
                  {showMoney && <th className="px-2 py-2 font-medium">Payout</th>}
                  {showStatus && <th className="px-2 py-2 font-medium">Type</th>}
                  {showSubs && SUBS.map((k) => <th key={k} className="px-2 py-2 font-medium">{k.replace("sub_id_", "S")}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const e = perRow[r.id] || {};
                  return (
                    <tr key={r.id} className="border-b border-white/[0.05] last:border-0" data-testid={`bulk-edit-row-${r.id}`}>
                      <td className="whitespace-nowrap px-2 py-1.5 font-mono text-muted-foreground">{r.order_id}</td>
                      {showDate && <td className="px-2 py-1.5"><input type="datetime-local" value={e.created_at || ""} onChange={(ev) => setRowCell(r.id, "created_at", ev.target.value)} className={`${cls} w-[170px]`} /></td>}
                      {showMoney && <td className="px-2 py-1.5"><input type="number" value={e.revenue ?? ""} onChange={(ev) => setRowCell(r.id, "revenue", ev.target.value)} className={`${cls} w-[76px] text-right`} /></td>}
                      {showMoney && <td className="px-2 py-1.5"><input type="number" value={e.payout ?? ""} onChange={(ev) => setRowCell(r.id, "payout", ev.target.value)} className={`${cls} w-[76px] text-right`} /></td>}
                      {showStatus && <td className="px-2 py-1.5"><select value={e.status || "lead"} onChange={(ev) => setRowCell(r.id, "status", ev.target.value)} className={`${cls} w-[84px]`}><option value="lead">Lead</option><option value="sale">Sale</option><option value="redep">Redep</option><option value="revshare">RevShare</option></select></td>}
                      {showSubs && SUBS.map((k) => <td key={k} className="px-2 py-1.5"><input value={e[k] || ""} onChange={(ev) => setRowCell(r.id, k, ev.target.value)} className={`${cls} w-[72px]`} /></td>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" disabled={busy} onClick={() => onOpenChange(false)} className="text-muted-foreground hover:text-foreground">Cancel</Button>
          <Button disabled={busy} onClick={save} data-testid="bulk-edit-save" className="gap-1.5 bg-white text-black hover:bg-white/90">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />} Save changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
